import {useIntl, defineMessages} from 'react-intl'
import {
    useCustomPreferenceWithLocale,
    useCustomPreference,
} from '../../../../hooks/use-custom-preference'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

const messages = defineMessages({
    purchasedate: {
        defaultMessage: 'Purchased on*',
        id: 'product_registration_fields.label.purchase_date',
    },
    uploadFile: {
        defaultMessage: 'Choose file',
        id: 'product_registration_fields.label.upload_file',
    },
    serialNumber: {
        defaultMessage: 'Serial number (optional)',
        id: 'product_registration_fields.label.serial_number',
    },
    source: {
        defaultMessage: 'Source',
        id: 'product_registration_fields.label.source',
    },
})

/**
 * A React hook that provides the field definitions for the product registration form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useProductRegistrationFields({
    form: {
        control,
        formState: {errors},
    },
    prefix = '',
}) {
    const {formatMessage} = useIntl()
    const IsProductRegistrationReason = useCustomPreferenceWithLocale(
        'showProductRegistrationReason',
    )
    const materialGroupRegexConfig = useCustomPreference('materialGroupRegexConfig')

    const fields = {
        purchasedate: {
            name: `${prefix}purchasedate`,
            label: 'pruchase_date',
            placeholder: formatMessage(messages.purchasedate),
            type: 'text',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter when you bought the product.',
                    id: 'product_registration_fields.error.please_enter_bought_in',
                }),
                pattern: {
                    value: /^\d{2}\/\d{2}\/\d{4}$/,
                    message: formatMessage({
                        defaultMessage: 'Date must be in the format: dd/mm/yyyy.',
                        id: 'product_registration_fields.error.invalid_date_format',
                    }),
                },
                validate: {
                    notFutureDate: (value) => {
                        const selectedDate = new Date(value)
                        const today = new Date()
                        return (
                            selectedDate <= today ||
                            formatMessage({
                                defaultMessage: 'Purchase date cannot be in the future.',
                                id: 'product_registration_fields.error.future_date',
                            })
                        )
                    },
                },
            },
            error: errors[`${prefix}purchasedate`],
            control,
            shouldRemoveLabel: true,
            variant: 'productRegistration',
            isProductRegistration: true,
            maxDate: new Date(),
        },
        uploadFile: {
            name: `${prefix}uploadFile`,
            label: formatMessage(messages.uploadFile),
            placeholder: '',
            type: 'file',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please attach the proof of purchase',
                    id: 'product_registration_fields.error.please_attach_file',
                }),
                validate: {
                    fileSize: (file) =>
                        file.size <= 5242880 ||
                        formatMessage({
                            defaultMessage: 'File size should not exceed 5MB.',
                            id: 'product_registration_fields.error.file_size_exceeded',
                        }),
                },
            },
            error: errors[`${prefix}uploadFile`],
            control,
        },
        serialNumber: {
            name: `${prefix}serialNumber`,
            label: '',
            placeholder: formatMessage(messages.serialNumber),
            type: 'text',
            rules: (() => {
                try {
                    const selectedProductRaw = !isServer
                        ? localStorage.getItem('selectedProduct')
                        : null
                    // Check if selectedProduct exists and is a valid JSON string
                    if (!selectedProductRaw) {
                        console.warn('selectedProduct is not in localStorage')
                        return {} // Return empty rules if no product found
                    }
                    const selectedProduct = JSON.parse(selectedProductRaw)

                    const materialGroup = selectedProduct?.representedProduct?.c_materialGroup
                    const regexMap = materialGroupRegexConfig || {}
                    const regexForGroup = regexMap?.[materialGroup] || regexMap?.default

                    if (!regexForGroup) {
                        console.warn(`No regex found for materialGroup: ${materialGroup}`)
                        return {} // Return empty rules if no regex found for the materialGroup
                    }

                    return {
                        pattern: {
                            value: new RegExp(regexForGroup),
                            message: formatMessage({
                                defaultMessage: 'Invalid serial number format.',
                                id: 'product_registration_fields.error.invalid_serial_number',
                            }),
                        },
                    }
                } catch (e) {
                    console.error('Error parsing selectedProduct from localStorage:', e)
                    return {} // Return empty rules if any error occurs
                }
            })(),
            error: errors[`${prefix}serialNumber`],
            control,
            shouldRemoveLabel: true,
            variant: 'productRegistration',
            isProductRegistration: true,
        },
        source: {
            name: `${prefix}source`,
            label: '',
            defaultValue: '',
            placeholder: 'Lorem ipsum',
            type: 'select',
            options:
                IsProductRegistrationReason?.map((reason) => ({
                    value: reason,
                    label: reason,
                })) || [],
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select where you made the purchase.',
                    id: 'product_registration_fields.error.select_source',
                }),
            },
            error: errors[`${prefix}source`],
            control,
            variant: 'productRegistration',
        },
    }

    return fields
}
